import React, { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const API_BASE = 'https://locrawl-api.findrawdogfood.workers.dev'

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

export default function Explore() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [viewport, setViewport] = useState({
    latitude: 34.0522,
    longitude: -118.2437,
    zoom: 10
  })

  const [experiences, setExperiences] = useState([])
  const [filteredExperiences, setFilteredExperiences] = useState([])
  const [selectedExperience, setSelectedExperience] = useState(null)
  const [filters, setFilters] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v12')
  const [showFilters, setShowFilters] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  const categories = [
    { id: 'all', name: 'All', icon: '🌍', color: 'gray' },
    { id: 'sunset', name: 'Sunset', icon: '🌅', color: 'orange' },
    { id: 'food', name: 'Food', icon: '🍜', color: 'green' },
    { id: 'photo', name: 'Photo', icon: '📸', color: 'purple' },
    { id: 'nature', name: 'Nature', icon: '🌲', color: 'emerald' },
    { id: 'culture', name: 'Culture', icon: '🏛️', color: 'blue' },
    { id: 'nightlife', name: 'Nightlife', icon: '🌃', color: 'indigo' },
    { id: 'action', name: 'Adventure', icon: '⚡', color: 'red' },
    { id: 'chill', name: 'Relaxing', icon: '😌', color: 'teal' }
  ]

  const mapStyles = [
    { id: 'streets-v12', name: 'Streets', icon: '🗺️' },
    { id: 'satellite-streets-v12', name: 'Satellite', icon: '🛰️' },
    { id: 'dark-v11', name: 'Dark', icon: '🌙' },
    { id: 'light-v11', name: 'Light', icon: '☀️' }
  ]

  // Fetch experiences from API
  const fetchExperiences = useCallback(async () => {
    try {
      setLoading(true)
      let url = `${API_BASE}/api/experiences?limit=200`
      
      if (filters.length > 0 && !filters.includes('all')) {
        url += `&tags=${filters.join(',')}`
      }

      const response = await fetch(url)
      if (!response.ok) {
        throw new Error('Failed to fetch experiences')
      }

      const data = await response.json()
      const validExperiences = data.experiences.filter(exp => exp.lat && exp.lon)
      setExperiences(validExperiences)
      setError(null)
      
      // Center map on first experience if available
      if (validExperiences.length > 0 && experiences.length === 0) {
        setViewport(prev => ({
          ...prev,
          longitude: validExperiences[0].lon,
          latitude: validExperiences[0].lat,
          zoom: 6
        }))
      }
    } catch (err) {
      console.error('Error fetching experiences:', err)
      setError('Failed to load experiences. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [filters, experiences.length])

  useEffect(() => {
    fetchExperiences()
    // Check if user is logged in
    const token = localStorage.getItem('accessToken')
    setIsLoggedIn(!!token)
    
    // Check URL params for initial filters
    const category = searchParams.get('category')
    if (category && category !== 'all') {
      setFilters([category])
    }
  }, [fetchExperiences, searchParams])

  useEffect(() => {
    filterExperiences()
  }, [experiences, searchQuery, filters])

  const filterExperiences = () => {
    let filtered = experiences
    
    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(exp => 
        exp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exp.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exp.city_name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }
    
    // Filter by categories
    if (filters.length > 0 && !filters.includes('all')) {
      filtered = filtered.filter(exp => {
        const tags = exp.tags || []
        return filters.some(filter => 
          tags.some(tag => tag.toLowerCase().includes(filter.toLowerCase()))
        )
      })
    }
    
    setFilteredExperiences(filtered)
  }

  const toggleFilter = (categoryId) => {
    if (categoryId === 'all') {
      setFilters([])
      setSearchParams({})
    } else {
      const newFilters = filters.includes(categoryId) 
        ? filters.filter(f => f !== categoryId)
        : [...filters, categoryId]
      setFilters(newFilters)
      
      // Update URL
      if (newFilters.length > 0) {
        setSearchParams({ category: newFilters.join(',') })
      } else {
        setSearchParams({})
      }
    }
  }

  const handleExperienceClick = (experience) => {
    setSelectedExperience(experience)
    // Center map on selected experience
    setViewport(prev => ({
      ...prev,
      latitude: experience.lat,
      longitude: experience.lon,
      zoom: Math.max(prev.zoom, 14)
    }))
  }

  const addToFavorites = async (experienceId) => {
    if (!isLoggedIn) {
      alert('Please log in to save favorites')
      return
    }
    
    try {
      const token = localStorage.getItem('accessToken')
      const response = await fetch(`${API_BASE}/api/user/favorites/${experienceId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        alert('Added to favorites!')
      }
    } catch (error) {
      console.error('Error adding to favorites:', error)
    }
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b z-30">
        <div className="px-4 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to="/" className="text-lg font-bold text-indigo-600 flex items-center">
              <span className="text-2xl mr-1">🗺️</span> Locrawl
            </Link>
            <div className="hidden md:block text-gray-400 text-sm">|</div>
            <h1 className="hidden md:block text-sm font-medium text-gray-700">Explore</h1>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {loading ? 'Loading...' : `${filteredExperiences.length} experiences`}
            </span>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="relative hidden md:block">
              <input
                type="text"
                placeholder="Search experiences..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center space-x-1 ${
                showFilters ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="hidden sm:inline">Filters</span>
              {filters.length > 0 && (
                <span className="bg-indigo-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {filters.length}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Category filters */}
        {showFilters && (
          <div className="px-4 py-2 border-t bg-gray-50">
            {/* Mobile search */}
            <div className="relative md:hidden mb-2">
              <input
                type="text"
                placeholder="Search experiences..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <svg className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => toggleFilter(category.id)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center space-x-1 ${
                    (category.id === 'all' && filters.length === 0) || filters.includes(category.id)
                      ? 'bg-indigo-100 text-indigo-800 border border-indigo-300'
                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm">{category.icon}</span>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
            
            {/* Map style selector */}
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-600 font-medium">Style:</span>
              {mapStyles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => setMapStyle(`mapbox://styles/mapbox/${style.id}`)}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors flex items-center space-x-1 ${
                    mapStyle.includes(style.id)
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm">{style.icon}</span>
                  <span className="hidden sm:inline">{style.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="mx-4 mb-3 p-3 bg-red-100 border border-red-300 rounded-md text-red-700">
            {error}
          </div>
        )}
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'w-80' : 'w-0'} transition-all duration-300 bg-white border-r border-gray-200 flex flex-col overflow-hidden z-20`}>
          {sidebarOpen && (
            <>
              <div className="p-3 border-b bg-gray-50">
                <h2 className="text-sm font-semibold text-gray-900">
                  {filteredExperiences.length} Experiences
                </h2>
                {searchQuery && (
                  <div className="text-xs text-gray-600 mt-0.5">
                    Results for "{searchQuery}"
                  </div>
                )}
              </div>
              
              <div className="flex-1 overflow-y-auto experience-list">
                {loading ? (
                  <div className="p-6 text-center">
                    <div className="animate-spin w-6 h-6 border-3 border-indigo-600 border-t-transparent rounded-full mx-auto mb-3"></div>
                    <p className="text-sm text-gray-600">Loading experiences...</p>
                  </div>
                ) : filteredExperiences.length === 0 ? (
                  <div className="p-6 text-center">
                    <div className="text-3xl mb-3">🔍</div>
                    <h3 className="text-sm font-medium text-gray-900 mb-1">No experiences found</h3>
                    <p className="text-xs text-gray-600">Try adjusting your search or filters</p>
                  </div>
                ) : (
                  <div>
                    {filteredExperiences.map((experience) => (
                      <div
                        key={experience.id}
                        onClick={() => handleExperienceClick(experience)}
                        className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedExperience?.id === experience.id ? 'bg-indigo-50 border-indigo-200' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <h3 className="font-medium text-sm text-gray-900 flex-1 pr-2">{experience.title}</h3>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              addToFavorites(experience.id)
                            }}
                            className="ml-1 text-gray-400 hover:text-red-500 transition-colors text-sm"
                          >
                            ❤️
                          </button>
                        </div>
                        <p className="text-xs text-indigo-600 mb-1">{experience.city_name}</p>
                        <p className="text-xs text-gray-600 line-clamp-2 mb-2">{experience.description}</p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-0.5 text-yellow-500">
                            {[...Array(5)].map((_, i) => (
                              <svg key={i} className="w-3 h-3 fill-current" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                            <span className="text-xs text-gray-500 ml-0.5">4.8</span>
                          </div>
                          
                          <div className="flex space-x-1">
                            {experience.tags?.slice(0, 2).map((tag, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Map container */}
        <div className="flex-1 relative">
          <Map
            mapboxAccessToken={MAPBOX_TOKEN}
            initialViewState={viewport}
            onMove={(evt) => setViewport(evt.viewState)}
            style={{ width: '100%', height: '100%' }}
            mapStyle={mapStyle}
          >
            {/* Navigation controls */}
            <NavigationControl position="top-right" showCompass={true} showZoom={true} />
            <GeolocateControl position="top-right" />
            <ScaleControl position="bottom-left" />
            
            {/* Experience markers */}
            {filteredExperiences.map((experience) => (
              <Marker
                key={experience.id}
                latitude={experience.lat}
                longitude={experience.lon}
                onClick={(e) => {
                  e.originalEvent.stopPropagation()
                  setSelectedExperience(experience)
                }}
              >
                <div className={`rounded-full w-6 h-6 flex items-center justify-center text-xs cursor-pointer transition-all duration-200 border-2 border-white shadow-lg ${
                  selectedExperience?.id === experience.id 
                    ? 'bg-red-500 text-white scale-125' 
                    : 'bg-indigo-600 text-white hover:bg-indigo-700 hover:scale-110'
                }`}>
                  <span className="text-xs">📍</span>
                </div>
              </Marker>
            ))}

            {/* Popup for selected experience */}
            {selectedExperience && (
              <Popup
                latitude={selectedExperience.lat}
                longitude={selectedExperience.lon}
                onClose={() => setSelectedExperience(null)}
                closeButton={false}
                closeOnClick={false}
                className="max-w-sm"
                offset={[0, -10]}
              >
                <div className="p-3">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-sm text-gray-900 flex-1 pr-2">{selectedExperience.title}</h3>
                    <button
                      onClick={() => setSelectedExperience(null)}
                      className="ml-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <p className="text-indigo-600 font-medium text-xs mb-1">{selectedExperience.city_name}</p>
                  <p className="text-gray-600 text-xs mb-2 leading-relaxed line-clamp-3">{selectedExperience.description}</p>
                  
                  {selectedExperience.tags && selectedExperience.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {selectedExperience.tags.slice(0, 3).map((tag, index) => (
                        <span 
                          key={index}
                          className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-xs rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {selectedExperience.best_time && (
                    <p className="text-xs text-gray-500 mb-2">
                      <strong>Best time:</strong> {selectedExperience.best_time}
                    </p>
                  )}

                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-0.5 text-yellow-500">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-3 h-3 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                      <span className="text-xs text-gray-500 ml-0.5">4.8</span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-1.5">
                    <Link
                      to={`/experience/${selectedExperience.id}`}
                      className="flex-1 bg-indigo-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-indigo-700 transition-colors text-center"
                    >
                      View Details
                    </Link>
                    <button
                      onClick={() => addToFavorites(selectedExperience.id)}
                      className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors text-xs"
                    >
                      ❤️
                    </button>
                    <button className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors text-xs">
                      📤
                    </button>
                  </div>
                </div>
              </Popup>
            )}
          </Map>

          {/* Loading overlay */}
          {loading && (
            <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center z-10">
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Loading experiences...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}